<script setup lang="ts">
import type { HTMLAttributes } from "vue";
import * as z from "zod";
import { useField } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { cn } from "~/lib/utils";

const model = defineModel<string | null>();
const { t } = useI18n()

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
    },
    class: {
        type: String as PropType<HTMLAttributes['class']>
    },
    input_class: {
        type: String as PropType<HTMLAttributes['class']>
    },
    required: {
        type: Boolean,
        default: true
    },
    name: {
        type: String,
        default: 'select'
    },
    initialValue: {
        type: String,
        default: ''
    },
    options: {
        type: Array as PropType<Array<{ value: string, label: string }>>,
        required: true
    },
    label: {
        type: String,
        default: ''
    }
})

const usedPlaceholder = computed(() => {
    return props.placeholder || t('Select option')
})

const validateSchema = ref()

watchEffect(() => {
    const allowedValues = props.options.map(option => option.value)
    
    validateSchema.value = z.enum(allowedValues as [string, ...string[]], {
        required_error: t('The field is required'),
        invalid_type_error: t('Invalid value selected for field')
    })

    return props.required ? validateSchema : validateSchema.value.optional();
})

const { setValue } = useField(props.name, computed(() => toTypedSchema(validateSchema.value)));

// Initialize with initial value if provided
onMounted(() => {
    if (props.initialValue) {
        setValue(props.initialValue, true);
        model.value = props.initialValue;
    }
});

// Watch for model changes
watch(model, (val) => {
    setValue(val, false)
})

watch(() => props.options, () => {
    setValue(null, false)
    model.value = null;
})

// Watch for initial value changes
watch(() => props.initialValue, (newValue) => {
    if (newValue && newValue !== model.value) {
        setValue(newValue, false);
        model.value = newValue;
    }
}, {immediate: true});

// Handler for select change
const handleSelectChange = (value: string) => {
    model.value = value;
}
</script>

<template>
    <UiFormField v-model="model" v-slot="{ componentField }" :name="name">
        <UiFormItem class="relative">
            <UiFormLabel v-if="label">
                <p class="mb-1">{{ $t(label) }} <span v-if="required" class="text-primaryblue-500">*</span></p>
            </UiFormLabel>
            <UiSelect
                    v-bind="componentField"
                    :value="model"
                    @update:value="handleSelectChange"
                    :disabled="disabled">
                <UiSelectTrigger :class="cn('ys-form-input', props.input_class, !model && 'text-sub')">
                    <UiSelectValue :placeholder="usedPlaceholder" />
                </UiSelectTrigger>
                <UiSelectContent>
                    <UiSelectItem 
                        v-for="option in options" 
                        :key="option.value" 
                        :value="option.value"
                    >
                        {{ $t(option.label) }}
                    </UiSelectItem>
                </UiSelectContent>
            </UiSelect>
            <UiFormMessage />
        </UiFormItem>
    </UiFormField>
</template>